<script lang="ts" setup>
import { ref, toRefs } from 'vue';
import Calendar from 'primevue/calendar';
import { useUtilizationStore } from '@/store';
import { useDateTime, usePersonTypes } from '@/composables';
import { SOWReportingSPTableInterface } from '@/types';

interface TimeUsageModalPropsInterface {
  sowData: SOWReportingSPTableInterface[];
}

const props = defineProps<TimeUsageModalPropsInterface>();
const { sowData } = toRefs(props);

const utilizationStore = useUtilizationStore();
const maxDate = ref<Date>(useDateTime().getMostRecentSunday());
const minDate = ref<Date>(new Date('2024-01-02'));
const dateSelect = ref<Date>(new Date(utilizationStore.serviceProviderSOWFilterDate));

const definePercentageClass = (sow_utilization: number) => {
  if (sow_utilization > 100) {
    return 'text-red-600';
  }

  if (sow_utilization < 50) {
    return 'text-orange-600';
  }

  return '';
};

const onDateSelect = async (value: Date) => {
  dateSelect.value = value;
  await utilizationStore.updateServiceProviderSOWFilterDate(dateSelect.value);
};
</script>

<template>
  <Card class="flex-none flex flex-column">
    <template #header>
      <div class="flex flex-wrap">
        <div class="flex mr-3 pt-2">
          <Icon name="query_stats" class="material-icons text-color-blue mr-2" />
          <h2 v-text="'SOW Reporting up to:'" />
        </div>
        <div class="flex">
          <Calendar
            v-model="dateSelect"
            show-icon
            :disabled-days="[1, 2, 3, 4, 5, 6]"
            icon-display="input"
            class="text-sm"
            date-format="MM dd, yy"
            :max-date="maxDate"
            :min-date="minDate"
            @date-select="onDateSelect"
          />
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        :value="sowData"
        data-id="contract_id"
        responsive-layout="scroll"
        sort-field="billed_hours_percent"
        :sort-order="-1"
      >
        <Column field="organization" header="Organization" sortable />
        <Column field="contract_quote_number" header="Contract" sortable />
        <Column v-if="!usePersonTypes.isClinician()" field="contract_type" header="Type" sortable />
        <Column field="sow_hours" header-class="relative" sortable>
          <template #header>
            <span>SOW Hrs/Wk</span>
            <Icon
              v-tooltip.top="
                'Hrs from the SOW for this contract. This is the maximum number of billable hours. SP can bill for actual hours worked, not exceeding this maximum, tracked as average hours/week throughout the school year. Billing is based on actual hours worked'
              "
              name="info"
              class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
            />
          </template>
        </Column>
        <Column field="weeks" header-class="relative" sortable>
          <template #header>
            <span>Weeks</span>
            <Icon
              v-tooltip.top="'# of weeks of documented events'"
              name="info"
              class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
            />
          </template>
        </Column>
        <Column field="billed_hours_total_wo_eval" header="Hrs (Non-Eval)" header-class="relative" sortable />
        <Column field="eval_hours" header="Eval Hrs" header-class="relative" sortable />
        <Column field="billed_hours_total" header-class="relative" sortable>
          <template #header>
            <span>Total Billed Hrs</span>
            <Icon
              v-tooltip.top="
                'Total number of hours billed for this contract. Excludes Eval Hrs for Per Student contracts.'
              "
              name="info"
              class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
            />
          </template>
        </Column>
        <Column field="billed_hours_diff" header="Hrs +/-" header-class="relative" sortable />
        <Column field="billed_hours_percent" header-class="relative" sortable>
          <template #header>
            <span>%</span>
            <Icon
              v-tooltip.top="'Comparison of Total Billed Hours to SOW Hrs/Wk x Weeks'"
              name="info"
              class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
            />
          </template>
          <template #body="{ data }">
            <span
              :class="definePercentageClass(data.billed_hours_percent)"
              v-text="`${data.billed_hours_percent ? data.billed_hours_percent + '%' : 'N/A'}`"
            />
          </template>
        </Column>
        <Column field="billed_hours_average" header-class="relative" sortable>
          <template #header>
            <span>Avg Hrs/Wk (prior 3 mths)</span>
            <Icon
              v-tooltip.top="'Average hours billed/week for prior 3 months'"
              name="info"
              class="absolute right-0 top-0 mt-2 mr-1 text-gray-400 text-base cursor-pointer"
            />
          </template>
          <template #body="{ data }">
            <span v-text="`${data.billed_hours_average ? data.billed_hours_average : 'N/A'}`" />
          </template>
        </Column>
        <template #empty>
          <span v-text="'No data to display'" />
        </template>
      </DataTable>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.text-red-600 {
  color: $red !important;
}

.text-orange-600 {
  color: $orange !important;
}
</style>
